/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {StatefulComponent} from '../../../stateful/stateful.component';
import {InputCriteriaCurrencyState} from './models/input-criteria-currency.state';
import {Subscription} from 'rxjs';
import {InputCriteriaCurrencyConfiguration} from './configurations/input-criteria-currency-configuration';
import {SelectDropdownOptionModel} from '../shared/select-dropdown/models/select-dropdown-option.model';
import {InputCriteriaCurrencyCodeEnum} from './configurations/input-criteria-currency-code.enum';
import {InputCriteriaCurrencyModel} from './models/input-criteria-currency.model';
import {InputCriteriaTypeMapper} from '../mappers/input-criteria-type.mapper';
import {GlobalSearchCriterionFilterModel} from '../../models/global-search-criterion-filter.model';
import {GlobalSearchCriteriaService} from '../../services/global-search-criteria.service';
import {WarningsEnum} from '../../../warnings/warnings.enum';
import {
    AbstractControl,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {MandatoryMessageTypeEnum} from '../../../input/service/mandatory-message-type.enum';
import {InputMessagesService} from '../../../input/service/input-messages.service';
import {InputTextComponent} from '../../../input/input-text/input-text.component';

@Component({
    selector: 'app-input-criteria-currency',
    templateUrl: './input-criteria-currency.component.html',
    styleUrls: ['./input-criteria-currency.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class InputCriteriaCurrencyComponent extends StatefulComponent<InputCriteriaCurrencyState> implements OnInit, OnDestroy {
    @Input() searchCriteriaCurrencyChange: EventEmitter<void>;
    @Output() searchCriteriaCurrencyReset = new EventEmitter<void>();

    @ViewChild('currencyInput') currencyInput: InputTextComponent;
    @ViewChild('extraCurrencyInput') extraCurrencyInput: InputTextComponent;

    inputCriteriaCurrencyConfiguration = InputCriteriaCurrencyConfiguration;
    inputCriteriaCurrencyCodeEnum = InputCriteriaCurrencyCodeEnum;

    currencyInputMessages = new Map<WarningsEnum, string[]>();
    extraCurrencyInputMessages = new Map<WarningsEnum, string[]>();
    validators: { [key: string]: ValidatorFn[] } = {
        currencyFormat: [Validators.pattern(/^\d+(\.\d{1,2})?$/)]
    };
    currencyFormatInputWarningMessageRegex = /[0-9]/;

    private subscriptions = new Subscription();

    constructor(private globalSearchService: GlobalSearchCriteriaService, private inputMessagesService: InputMessagesService) {
        super();
    }

    ngOnInit(): void {
        this.subscriptions.add(this.searchCriteriaCurrencyChange?.subscribe(() => {
            this.clearTypedCurrency();
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    selectedOptionHandler(option: SelectDropdownOptionModel): void {
        this.storeState({
            selectedCriteriaCurrency: option,
            defaultValue: null,
            extraValue: null
        });
    }

    manageDefaultInputStatusChange = (value: AbstractControl, isExtraInput?: boolean): void => {
        if (isExtraInput) {
            const inputExtraValue = document
                .querySelector('[data-cy=input-criteria-currency-extra-input]')?.getAttribute('initial-value');
            this.inputMessagesService?.mandatoryWarnings(value, this.extraCurrencyInputMessages, MandatoryMessageTypeEnum.APPLY);
            this.extraCurrencyInputMessages = new Map<WarningsEnum, string[]>(this.extraCurrencyInputMessages);
            this.inputMessagesService.invalidInputValueError(value,
                    this.extraCurrencyInputMessages,
                    this.inputCriteriaCurrencyConfiguration.currencyFormatErrorMessage);
            if (!this.currencyFormatInputWarningMessageRegex.test(inputExtraValue) && inputExtraValue != null && inputExtraValue !== '') {
                this.inputMessagesService?.mandatoryWarnings(value, this.extraCurrencyInputMessages, MandatoryMessageTypeEnum.NUMERIC);
            } else if (!this.isEmpty(this.state.extraValue) && inputExtraValue !== '') {
                this.extraCurrencyInputMessages.delete(WarningsEnum.WARNING);
            }
        } else {
            const inputDefaultValue = document
                .querySelector('[data-cy=input-criteria-currency-default-input]')?.getAttribute('initial-value');
            this.inputMessagesService?.mandatoryWarnings(value, this.currencyInputMessages, MandatoryMessageTypeEnum.APPLY);
            this.currencyInputMessages = new Map<WarningsEnum, string[]>(this.currencyInputMessages);
            this.inputMessagesService.invalidInputValueError(value,
                    this.currencyInputMessages,
                    this.inputCriteriaCurrencyConfiguration.currencyFormatErrorMessage);
            if (!this.currencyFormatInputWarningMessageRegex.test(inputDefaultValue)
                && inputDefaultValue != null && inputDefaultValue !== '') {
                    this.inputMessagesService?.mandatoryWarnings(value, this.currencyInputMessages, MandatoryMessageTypeEnum.NUMERIC);
            } else if (!this.isEmpty(this.state.defaultValue) && inputDefaultValue !== ''){
                    this.currencyInputMessages.delete(WarningsEnum.WARNING);
            }
        }
    };

    setDefaultValue(value: string): void {
        this.storeState({
            defaultValue: value
        });
    }

    setExtraValue(value: string): void {
        this.storeState({
            extraValue: value
        });
    }

    submitCurrencyCriteria(): void {
        if (this.state.selectedCriteriaCurrency.code === InputCriteriaCurrencyCodeEnum.BETWEEN) {
            this.inputWarningMessageBetweenOption();
        } else {
            this.inputWarningMessageAllOptions();
        }
    }

    private inputWarningMessageBetweenOption(): void {
        if (this.state.defaultValue != null && this.isEmpty(this.state.extraValue)) {
            this.extraCurrencyInput?.setValue('');
        } else if (this.state.extraValue != null && this.isEmpty(this.state.defaultValue)) {
            this.currencyInput?.setValue('');
        } else if (this.isEmpty(this.state.defaultValue) && this.isEmpty(this.state.extraValue)) {
            this.currencyInput?.setValue('');
            this.extraCurrencyInput?.setValue('');
        } else if (this.currencyInput.isValid && this.extraCurrencyInput.isValid) {
            this.generateQueryFilter();
        }
    }

    private inputWarningMessageAllOptions(): void {
        if (this.state.defaultValue != null && !this.isEmpty(this.state.defaultValue)) {
            if (this.currencyInput.isValid) {
                this.generateQueryFilter();
            }
        } else {
            this.currencyInput?.setValue('');
        }
    }

    private isEmpty(value: string): boolean {
        return value == null || value === '';
    }

    private generateQueryFilter(): void {
        const criterionFilterWithDateSuffix: GlobalSearchCriterionFilterModel = {
            ...this.globalSearchService.getSelectedSearchCriterionFilter(),
            criterionSuffix: this.state.selectedCriteriaCurrency?.resultPrefix?.toLowerCase()
        };

        const queryFilter = InputCriteriaTypeMapper.fromInputCriteriaTypeToQueryChipsAreaModel(
                criterionFilterWithDateSuffix,
                this.getQueryFilterValue(),
                this.getInputCurrencyCriteriaValuesMapper());

        if (queryFilter) {
            this.globalSearchService.addCriterionFilterToQuery(queryFilter);
            this.searchCriteriaCurrencyReset.emit();
        }
    }

    private getQueryFilterValue(): string {
        let formattedValue = '';

        const formattedDefaultValue = this.getFormattedInputValue(this.state.defaultValue);
        const formattedExtraValue = this.getFormattedInputValue(this.state.extraValue);
        const unit = this.inputCriteriaCurrencyConfiguration.unit;

        if (this.state.selectedCriteriaCurrency?.code === InputCriteriaCurrencyCodeEnum.BETWEEN) {
            formattedValue = `${formattedDefaultValue} ${unit} and ${formattedExtraValue} ${unit}`;
        } else {
            formattedValue = `${formattedDefaultValue} ${unit}`;
        }
        return formattedValue;
    }

    private getFormattedInputValue(input: string): string {
        return input?.charAt(input.length - 1) === '.' ? input.slice(0, -1) : input;
    }

    private getInputCurrencyCriteriaValuesMapper(): InputCriteriaCurrencyModel {
        const defaultValueNumber = parseFloat(this.state.defaultValue);
        const extraValueNumber = parseFloat(this.state.extraValue);

        let biggerThan: number = null;
        let lesserThan: number = null;

        switch (this.state.selectedCriteriaCurrency?.code) {
            case InputCriteriaCurrencyCodeEnum.EQUAL_TO:
                biggerThan = defaultValueNumber;
                lesserThan = defaultValueNumber;
                break;
            case InputCriteriaCurrencyCodeEnum.BIGGER_THAN:
                biggerThan = defaultValueNumber + this.inputCriteriaCurrencyConfiguration.filterIntervalNumber;
                break;
            case InputCriteriaCurrencyCodeEnum.EQUAL_OR_BIGGER_THAN:
                biggerThan = defaultValueNumber;
                break;
            case InputCriteriaCurrencyCodeEnum.SMALLER_THAN:
                lesserThan = defaultValueNumber - this.inputCriteriaCurrencyConfiguration.filterIntervalNumber;
                break;
            case InputCriteriaCurrencyCodeEnum.EQUAL_OR_SMALLER_THAN:
                lesserThan = defaultValueNumber;
                break;
            case InputCriteriaCurrencyCodeEnum.BETWEEN:
                biggerThan = defaultValueNumber + this.inputCriteriaCurrencyConfiguration.filterIntervalNumber;
                lesserThan = extraValueNumber - this.inputCriteriaCurrencyConfiguration.filterIntervalNumber;
                break;
        }
        return {biggerThan: biggerThan, lesserThan: lesserThan};
    }

    private clearTypedCurrency(): void {
        this.storeState({
            selectedCriteriaCurrency: null,
            extraValue: null,
            defaultValue: null
        });
    }
}
